// client/src/components/pages/ErrorDetection/NotificationModal.tsx (converted from .js, WBE
// batch ST-00223)
/**
 * Notification Settings Modal
 * Manage email notification rules for error detection alerts.
 * Add/remove notification recipients with severity thresholds.
 *
 * Everyone who can see Error Detection sees who gets a rule's alerts. Adding or removing a
 * recipient needs errors.manage (the server enforces it): an alert e-mails the tenant's
 * ticket rows to whatever address is typed in.
 */

import { useState, useEffect } from 'react';
import { X, Bell, Plus, Trash2, Mail, RefreshCw, AlertTriangle, Lock } from 'lucide-react';
import api from '../../../services/api';
import { SeverityBadge } from './ErrorCard';
import ThemedSelect from '../../common/ThemedSelect';
import { useAuth } from '../../../types/commonComponents';
import type {
  ErrorDetectionResult,
  NotificationSetting,
} from '../../../types/clientPages/errorDetection';

interface NotificationModalProps {
  errorResult: Pick<ErrorDetectionResult, 'rule_id' | 'rule_name' | 'severity_thresholds'>;
  onClose: () => void;
  /** Kept for the page's wiring; the window saves each change as it is made. */
  onSave?: () => void;
}

const NotificationModal = ({ errorResult, onClose }: NotificationModalProps) => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('errors.manage');
  const [notifications, setNotifications] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thresholds = errorResult.severity_thresholds || {};

  const [newNotification, setNewNotification] = useState({
    emailAddresses: '',
    notifyOnSeverity: 'warning',
  });

  // Load existing notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await api.errorDetection.getNotifications(errorResult.rule_id);
        setNotifications(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [errorResult.rule_id]);

  // Email validation
  const validateEmails = (str: string) => {
    if (!str.trim()) return false;
    const emails = str
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.length > 0 && emails.every((e) => re.test(e));
  };

  const handleAdd = async () => {
    if (!validateEmails(newNotification.emailAddresses)) {
      setError('Please enter valid email addresses');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.errorDetection.createNotification({
        ruleId: errorResult.rule_id,
        emailAddresses: newNotification.emailAddresses.trim(),
        notifyOnSeverity: newNotification.notifyOnSeverity,
      });

      // Refresh list
      const response = await api.errorDetection.getNotifications(errorResult.rule_id);
      setNotifications(response.data);
      setNewNotification({ emailAddresses: '', notifyOnSeverity: 'warning' });
    } catch (err) {
      console.error('Error creating notification:', err);
      setError('Failed to create notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      await api.errorDetection.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.setting_id !== notificationId));
      setError(null);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  const isFormValid =
    newNotification.emailAddresses.trim() && validateEmails(newNotification.emailAddresses);

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-500">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2
                className="text-lg font-bold text-slate-900"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Notification Settings
              </h2>
              <p className="text-xs text-slate-500">{errorResult.rule_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 animate-fadeIn">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Add new notification — errors.manage only */}
          {!canManage && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
              <span>
                Only users who can manage Error Detection can add or remove alert e-mails. Ask one
                of them to add you.
              </span>
            </div>
          )}
          {canManage && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                Add Email Alert
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Email Addresses
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={newNotification.emailAddresses}
                    onChange={(e) => {
                      setNewNotification((prev) => ({ ...prev, emailAddresses: e.target.value }));
                      if (error) setError(null);
                    }}
                    placeholder="email@example.com, another@example.com"
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all bg-surface"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Separate multiple addresses with commas
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Trigger Severity
                </label>
                <ThemedSelect
                  value={newNotification.notifyOnSeverity}
                  onChange={(e) =>
                    setNewNotification((prev) => ({
                      ...prev,
                      notifyOnSeverity: String(e.target.value),
                    }))
                  }
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 appearance-none cursor-pointer transition-all bg-surface"
                >
                  <option value="warning">Warning or Higher</option>
                  <option value="critical">Critical or Higher</option>
                  <option value="armageddon">Armageddon Only</option>
                </ThemedSelect>
              </div>

              <button
                onClick={handleAdd}
                disabled={saving || !isFormValid}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Notification
                  </>
                )}
              </button>
            </div>
          )}

          {/* Existing notifications */}
          <div>
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-3">
              Active Notifications
            </p>

            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 rounded-xl border border-dashed border-slate-200">
                <Bell className="mx-auto w-8 h-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No notifications configured</p>
                {canManage && (
                  <p className="text-xs text-slate-400 mt-0.5">Add an email alert above</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <div
                    key={notif.setting_id}
                    className="flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-surface hover:shadow-sm transition-all group"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <SeverityBadge severity={notif.notify_on_severity} />
                        <span className="text-[10px] text-slate-400">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 truncate">
                        <span className="font-medium">To:</span> {notif.email_addresses}
                      </p>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => handleDelete(notif.setting_id)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0 ml-2"
                        title="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Severity reference */}
          <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-3.5">
            <p className="text-[10px] font-semibold text-primary-700 uppercase tracking-widest mb-2">
              Thresholds for "{errorResult.rule_name}"
            </p>
            <div className="grid grid-cols-3 gap-3 text-xs text-center">
              <div>
                <span className="inline-flex items-center gap-1 font-semibold text-amber-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Warning
                </span>
                <p
                  className="text-slate-600 font-medium mt-0.5"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {thresholds.warning || '?'}+ errors
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-1 font-semibold text-orange-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                  Critical
                </span>
                <p
                  className="text-slate-600 font-medium mt-0.5"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {thresholds.critical || '?'}+ errors
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-1 font-semibold text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Armageddon
                </span>
                <p
                  className="text-slate-600 font-medium mt-0.5"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {thresholds.armageddon || '?'}+ errors
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
